// PR-P0-5 reference: UC-5 (Questionnaires — paste/upload → questions extraction) · no ADR · docs/feature-status-and-gaps.md §2.5
// Mockup: .claude/skills/ux-adversary/runs/uc-5-questionnaires/
// Systems Architecture: §5.13
import { QuestionnaireService } from './questionnaire-service.js';
import { defineTemplate, field } from '../egress/data-classes.js';
import { detectRecognizer, liftRecognizerQuestions, loadPublishedRecognizers, } from './questionnaire-recognizer-service.js';
// Locally-scoped RNG helper — same shape as questionnaire-service.
function cryptoRandom() {
    const g = globalThis;
    if (typeof g === 'object' &&
        g !== null &&
        'crypto' in g &&
        typeof g.crypto?.randomUUID ===
            'function') {
        return g.crypto
            .randomUUID()
            .replace(/-/g, '')
            .slice(0, 16);
    }
    return Math.random().toString(36).slice(2, 18);
}
const VALID_TYPES = new Set([
    'yes_no',
    'short',
    'long',
    'evidence_upload',
    'multi_select',
    'unknown',
]);
/**
 * Instruction + JSON-schema hint for the extract stage's Workers AI call.
 * Kept as a const so tests can snapshot the exact prompt (LLM extract
 * behavior is prompt-sensitive; changes to this string ARE product changes).
 */
export const EXTRACT_INSTRUCTIONS = [
    'You are a compliance analyst. Extract every distinct question in the input.',
    'Preserve numbering (`seq_no`, 1-indexed).',
    'If a question sits under a section heading (e.g., "IS.01 Information Security"),',
    'set `section` to the heading id or short name.',
    'Classify each question\'s expected answer type as one of:',
    '  yes_no, short, long, evidence_upload, multi_select, unknown.',
    'Prose text between questions is NOT a question — skip it.',
].join(' ');
/** ADR-0073: the extract prompt as a reviewed, static template (the questionnaire text is the classified input). */
export const EXTRACT_TEMPLATE = defineTemplate('questionnaire.extract', 'v1', EXTRACT_INSTRUCTIONS, []);
export const EXTRACT_SCHEMA = {
    questions: [
        { seq_no: 1, section: 'string|null', question_type: 'yes_no|short|long|evidence_upload|multi_select|unknown', text: 'string' },
    ],
};
/**
 * Fallback line-splitter. Used when the LLM extract returns low confidence
 * or an unparseable payload. Splits on lines that start with a number-dot
 * ("1. ...", "12) ..."), a lettered enumeration, or a lone question mark.
 * Never returns zero rows if input contains any non-blank content —
 * degrades to one giant row so the reviewer sees the paste in the queue.
 */
export function lineSplit(text) {
    const trimmed = text.trim();
    if (!trimmed)
        return [];
    const lines = trimmed.split(/\r?\n/);
    const NUM_RE = /^\s*(?:\d+[\.\)]|[A-Z]\.)\s+(.*)/;
    const rows = [];
    let current = null;
    for (const line of lines) {
        const m = NUM_RE.exec(line);
        if (m) {
            if (current)
                rows.push(mkRow(rows.length + 1, current.join(' ').trim()));
            current = [m[1]];
        }
        else if (current) {
            // Continuation line for the previous numbered question.
            const cont = line.trim();
            if (cont)
                current.push(cont);
        }
    }
    if (current)
        rows.push(mkRow(rows.length + 1, current.join(' ').trim()));
    if (rows.length === 0) {
        // No numbered structure at all — return the whole paste as one row so
        // the reviewer decides what to do rather than getting an empty extract.
        rows.push(mkRow(1, trimmed));
    }
    return rows;
}
function mkRow(seq, text) {
    return { seq_no: seq, section: null, question_type: 'unknown', text };
}
function coerceType(raw) {
    if (typeof raw !== 'string')
        return 'unknown';
    const lower = raw.toLowerCase().replace(/[-\s]/g, '_');
    return VALID_TYPES.has(lower) ? lower : 'unknown';
}
function normalizeLlmQuestions(payload) {
    if (!payload || typeof payload !== 'object')
        return [];
    const arr = payload.questions;
    if (!Array.isArray(arr))
        return [];
    const rows = [];
    for (let i = 0; i < arr.length; i++) {
        const q = arr[i];
        if (!q || typeof q.text !== 'string' || !q.text.trim())
            continue;
        rows.push({
            seq_no: typeof q.seq_no === 'number' && q.seq_no > 0 ? Math.floor(q.seq_no) : i + 1,
            section: typeof q.section === 'string' && q.section.trim() ? q.section.trim() : null,
            // Accept `type` as an alias for `question_type` — small LLM regularly picks the shorter key.
            question_type: coerceType(q.question_type ?? q.type),
            text: q.text.trim(),
        });
    }
    // Deduplicate seq_no collisions (LLM sometimes repeats 1 for every row).
    const seen = new Set();
    return rows.map((r, i) => {
        if (seen.has(r.seq_no))
            r = { ...r, seq_no: i + 1 };
        seen.add(r.seq_no);
        return r;
    });
}
/**
 * LLM extract — schema-forced JSON, through the egress gateway (ADR-0073).
 * Kept separate from persistence so tests can drive it with a test gateway
 * and inspect the exact returned rows. A refused call (federal mode with no
 * eligible provider) takes the deterministic line-splitter (parity:
 * questionnaire_import → deterministic).
 */
export async function extractQuestionsWithLlm(llm, text) {
    const result = await llm.run({
        task: 'questionnaire.extract', template: EXTRACT_TEMPLATE, input: field('questionnaire.document_text', text), schemaHint: EXTRACT_SCHEMA,
    });
    if (result.status !== 'ok') {
        return { questions: lineSplit(text), confidence: 0.3, usedFallback: true };
    }
    const parsed = normalizeLlmQuestions(result.output);
    if (parsed.length === 0 || result.confidence < 0.5) {
        return { questions: lineSplit(text), confidence: Math.max(result.confidence, 0.3), usedFallback: true };
    }
    return { questions: parsed, confidence: result.confidence, usedFallback: false };
}
/**
 * Batch insert of extracted questions.
 *
 * Uses INSERT ... SELECT joined against `questionnaires` so:
 *   1. The SQL mentions `workspace_id`, so WorkspaceScopedDatabase accepts it.
 *   2. The insert atomically refuses if the questionnaire isn't in this
 *      workspace — no ownership-check race window.
 *   3. Child table `questionnaire_questions` has no `workspace_id` column
 *      of its own (per plan §2); the scoping is authoritative via this join.
 *
 * Idempotency: extract stage runs exactly once per row per plan §4. If
 * duplicates arrive, the UNIQUE (questionnaire_id, seq_no) constraint
 * rejects the batch.
 */
export async function persistExtractedQuestions(db, questionnaireId, questions, extractedVia) {
    if (questions.length === 0)
        return;
    const now = new Date().toISOString();
    const stmts = questions.map((q) => ({
        sql: `INSERT INTO questionnaire_questions (
            question_id, questionnaire_id, seq_no, section,
            question_type, text, extracted_via, created_at
          )
          SELECT ?, questionnaire_id, ?, ?, ?, ?, ?, ?
            FROM questionnaires
           WHERE questionnaire_id = ? AND workspace_id = ?`,
        params: [
            `qq-${cryptoRandom()}`,
            q.seq_no,
            q.section,
            q.question_type,
            q.text,
            extractedVia,
            now,
            questionnaireId,
            db.workspaceId,
        ],
    }));
    // batch() runs statements together but each is scope-checked individually.
    for (const s of stmts) {
        const res = await db.execute(s.sql, s.params);
        if (!res.meta?.changes) {
            throw new Error(`persistExtractedQuestions: questionnaire ${questionnaireId} not in workspace ${db.workspaceId} (or was deleted mid-insert)`);
        }
    }
}
export class QuestionnaireExtractor {
    db;
    llm;
    constructor(db, llm) {
        this.db = db;
        this.llm = llm;
    }
    async process(questionnaireId, options = {}) {
        const svc = new QuestionnaireService(this.db);
        const row = await svc.get(questionnaireId);
        if (!row)
            throw new Error(`Extractor: no questionnaire ${questionnaireId}`);
        if (row.status !== 'extracting') {
            throw new Error(`Extractor: questionnaire ${questionnaireId} is in status ${row.status}, not 'extracting'`);
        }
        let questions;
        let extractedVia;
        let llmConfidence = null;
        let usedFallback = false;
        if (options.explicitQuestions) {
            questions = options.explicitQuestions;
            extractedVia = options.explicitExtractedVia ?? 'llm';
        }
        else if (row.template_id) {
            const recognized = await this.tryLiftFromTemplate(row);
            if (recognized) {
                questions = recognized;
                extractedVia = 'recognizer';
            }
            else {
                // Template tagged the paste but has no question map (SIG-Lite path).
                const result = await this.runLlmOrFallback(row);
                questions = result.questions;
                llmConfidence = result.confidence;
                usedFallback = result.usedFallback;
                extractedVia = 'llm';
            }
        }
        else {
            const result = await this.runLlmOrFallback(row);
            questions = result.questions;
            llmConfidence = result.confidence;
            usedFallback = result.usedFallback;
            extractedVia = 'llm';
        }
        await persistExtractedQuestions(this.db, questionnaireId, questions, extractedVia);
        await svc.updateStatus(questionnaireId, 'matching');
        return { questions, extractedVia, llmConfidence, usedFallback };
    }
    async runLlmOrFallback(row) {
        if (!row.source_text) {
            // XLSX rows have no source_text — those go through the mapping-confirm
            // path with `explicitQuestions`. If we ever get here for an XLSX row,
            // the pipeline is being called out of order.
            throw new Error(`Extractor: questionnaire ${row.questionnaire_id} has no source_text and no explicitQuestions`);
        }
        return extractQuestionsWithLlm(this.llm, row.source_text);
    }
    async tryLiftFromTemplate(row) {
        if (!row.template_id)
            return null;
        const res = await this.db
            .unscoped()
            .query(`SELECT * FROM questionnaire_templates WHERE template_id = ? LIMIT 1`, [row.template_id]);
        const tpl = res.results[0];
        if (!tpl)
            return null;
        const lifted = liftRecognizerQuestions(tpl);
        if (!lifted)
            return null;
        return lifted.map(mapRecognizerToExtracted);
    }
}
function mapRecognizerToExtracted(r) {
    return {
        seq_no: r.seq_no,
        section: r.section ?? null,
        question_type: (r.type ?? 'unknown'),
        text: r.text,
    };
}
/**
 * Ingest-time recognizer detection: called from the paste-create route
 * BEFORE persisting so the questionnaire row can carry `template_id` from
 * the start. Returns the matched template's id, or null.
 */
export async function detectRecognizerForPaste(db, text) {
    const recs = await loadPublishedRecognizers(db);
    const hit = detectRecognizer(text, recs);
    return hit?.template_id ?? null;
}
//# sourceMappingURL=questionnaire-extractor-service.js.map